/**
 * Split-screen auth layout — premium luxury treatment.
 *
 * Layout
 *  ─────────────────────────────────────────────────────────────
 *   xs  (   0– 599)  Image hidden. Form full-width on ivory.
 *   sm  ( 600– 899)  Image hidden. Form centred, more breathing room.
 *   md  ( 900–1199)  Split 46 / 54  (image / form).
 *   lg+ (1200+    )  Split 48 / 52.
 *
 * Image presentation
 *   - <img> with mild filter (brightness/saturate/contrast) so the
 *     bottle keeps its natural look + a touch of polish.
 *   - Soft top-left studio highlight (mix-blend-mode soft-light)
 *     reads as a reflection on the glass.
 *   - Long horizontal alpha mask fades the image to transparent so
 *     the panel's IVORY bg shows through without any colour-mixing
 *     band ever forming.
 *
 * Premium pass adds:
 *   - HY monogram watermark in the bottom-right of the form panel
 *   - Editorial micro-details around the brand block: gold-flourish
 *     diamond + EST. line in small caps
 *   - Bottom editorial footer with hairline rule + copyright
 *   - Staggered fade-in animation via framer-motion
 *
 * Typography
 *   Inter Semibold for the page heading (matches the dashboard's
 *   section-heading treatment), Playfair only on the monogram.
 */

import { Box, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

interface AuthLayoutProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

const IVORY = '#faf6ee';
const INK = '#0a0705';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';

const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

// Stagger animation: each child eases in after the previous, ~80 ms
// apart, 420 ms each. Reads as quiet luxury, not a SaaS-y wipe.
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: 'easeOut' as const } },
};

const MotionBox = motion.create(Box);

// Orientation-aware breakpoint: the split (image-left / form-right)
// layout only activates on viewports that are BOTH at least md-wide
// AND landscape-oriented (aspect-ratio ≥ 1).  Otherwise — phones,
// iPad portrait, anything narrower — the layout uses the banner mode
// (image-on-top / form-below).  This prevents the wide landscape
// hero image from being squeezed into a tall narrow portrait slice
// when, e.g., iPad Pro is held in portrait (1024 × 1366).
const SPLIT_QUERY = '@media (min-width: 900px) and (min-aspect-ratio: 1/1)';

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        // Default to column (banner mode).  Switch to row only when
        // the viewport is large AND landscape — see SPLIT_QUERY.
        flexDirection: 'column',
        [SPLIT_QUERY]: { flexDirection: 'row' },
        bgcolor: IVORY,
        overflow: 'hidden',
      }}
    >
      {/* ===========================================================
       *  IMAGE PANEL
       *
       *  On md+: occupies the left 46–48% as a full-height column.
       *  On xs / sm: collapses into a top hero banner so the brand
       *  imagery is always present — every viewport gets the same
       *  premium first impression, just composed differently.
       *
       *  ObjectPosition adapts per orientation so the bottle + glass
       *  stay nicely framed in either landscape (banner) or portrait
       *  (column) crop.
       * ========================================================= */}
      <Box
        sx={{
          // Banner mode (default): full width + a fixed height tuned
          // per device so the bottle+glass read as a composed scene.
          //   xs (phone):   200px
          //   sm (≥600):    260px — bigger tablet banners
          //   md (≥900) portrait: 320px — for iPad Pro portrait, etc.
          // Side-by-side mode: 46% / 48% of the viewport (via SPLIT_QUERY)
          width: '100%',
          height: { xs: 200, sm: 260, md: 320 },
          [SPLIT_QUERY]: {
            width: { md: '46%', lg: '48%' },
            height: 'auto',
          },
          flexShrink: 0,
          position: 'relative',
          bgcolor: INK,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/auth-hero.png"
          alt="Hooten Young — premium American whiskey"
          loading="eager"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Landscape banner crops favour the centre-bottom (bottle
            // base + glass + label).  Side-by-side keeps the original
            // centre crop which works for the full-height column.
            objectPosition: 'center 68%',
            display: 'block',
            filter: 'brightness(1.05) saturate(1.10) contrast(1.03)',
            // Soft mask that fades the image to transparent on the
            // edge facing the form — bottom in banner mode, right in
            // split mode.  This blends the image into the cream form
            // panel instead of cutting against a hard seam.  The gold
            // pinstripe stays as a refined accent on top.
            WebkitMaskImage:
              'linear-gradient(to bottom, black 0%, black 78%, rgba(0,0,0,0.5) 92%, transparent 100%)',
            maskImage:
              'linear-gradient(to bottom, black 0%, black 78%, rgba(0,0,0,0.5) 92%, transparent 100%)',
            [SPLIT_QUERY]: {
              objectPosition: 'center',
              WebkitMaskImage:
                'linear-gradient(to right, black 0%, black 82%, rgba(0,0,0,0.6) 94%, transparent 100%)',
              maskImage:
                'linear-gradient(to right, black 0%, black 82%, rgba(0,0,0,0.6) 94%, transparent 100%)',
            },
          }}
        />
        {/* Subtle studio-light highlight on the bottle's shoulder */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(
              ellipse 55% 65% at 28% 22%,
              rgba(255, 245, 220, 0.10) 0%,
              transparent 60%
            )`,
            mixBlendMode: 'soft-light',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* ===========================================================
       *  SEAM — gold pinstripe
       *
       *  Horizontal on small screens (sits between the top banner
       *  and the form below); vertical on md+ (between the two
       *  columns).  Either way it reads as a luxury foil-stamp inlay.
       * ========================================================= */}
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          // Banner mode (default): horizontal hairline between image
          // and form.  Split mode: vertical pinstripe between columns.
          width: '100%',
          height: '2px',
          background: `linear-gradient(
            to right,
            transparent 0%,
            ${GOLD} 30%,
            ${GOLD_LIGHT} 50%,
            ${GOLD} 70%,
            transparent 100%
          )`,
          opacity: 0.7,
          [SPLIT_QUERY]: {
            width: '2px',
            height: 'auto',
            background: `linear-gradient(
              to bottom,
              transparent 0%,
              ${GOLD} 20%,
              ${GOLD_LIGHT} 50%,
              ${GOLD} 80%,
              transparent 100%
            )`,
            opacity: 0.8,
          },
        }}
      />

      {/* ===========================================================
       *  RIGHT — form panel
       *
       *  Content anchors to the upper-middle (~14vh from top) so the
       *  layout reads as "this is where you sign in" rather than
       *  drifting symmetrically in dead-center emptiness. The HY
       *  monogram sits in the bottom-right corner as a small
       *  letterhead seal, completing the composition without
       *  competing with the form.
       *
       *  A near-imperceptible SVG paper-grain overlay (fractal
       *  noise, ~3% opacity) gives the ivory the tactile dimension
       *  real luxury print materials have — invisible until you
       *  compare side-by-side with a perfectly flat surface, but
       *  registers in the eye as "this surface has weight".
       * ========================================================= */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          position: 'relative',
          bgcolor: IVORY,
          display: 'flex',
          // Banner mode: vertically-centre the form in the remaining
          // space below the banner so there's no void at the bottom.
          // Split mode: anchor at upper-middle, more deliberate.
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8, lg: 10 },
          py: { xs: 4, sm: 5, md: 6 },
          [SPLIT_QUERY]: {
            alignItems: 'flex-start',
            pt: { md: '15vh', lg: '16vh' },
            pb: { md: 6 },
          },
          // Paper-grain overlay. The data-URI SVG generates a
          // 200×200 noise tile that repeats — small enough that
          // there's no visible seam. Multiply blend mode integrates
          // it with the ivory bg without changing the perceived
          // colour.
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
            backgroundSize: '200px 200px',
            mixBlendMode: 'multiply',
            opacity: 0.06,
            zIndex: 0,
          },
        }}
      >
        {/* HY brand monogram seal — uses the actual sword-style
            monogram from the bottle (public/brand/hy-monogram-white.png)
            as a CSS mask so we can tint it brand gold. Reads as the
            same mark stamped on the product itself — the page IS the
            brand. Opacity is high enough that the monogram has real
            visual presence (a foil-stamp seal, not a faded watermark)
            but low enough that the form remains the focal point. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            // Pushed deep into the corner so it can't bleed into the
            // form area at any standard laptop / desktop resolution.
            right: { md: 20, lg: 28, xl: 40 },
            bottom: { md: 32, lg: 44, xl: 60 },
            width: { md: 64, lg: 84, xl: 108 },
            height: { md: 84, lg: 110, xl: 140 },
            bgcolor: '#a67a35',
            opacity: 0.26,
            WebkitMaskImage: 'url(/brand/hy-monogram-white.png)',
            maskImage: 'url(/brand/hy-monogram-white.png)',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            userSelect: 'none',
            pointerEvents: 'none',
            zIndex: 0,
            // Only show in split mode where there's a real corner
            // available — hidden in banner mode to keep the form
            // surface clean.
            display: 'none',
            [SPLIT_QUERY]: { display: { md: 'none', lg: 'block' } },
          }}
        />

        {/* Form content — staggered fade-in */}
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="show"
          sx={{
            width: '100%',
            maxWidth: { xs: 380, sm: 440, lg: 460 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Stack spacing={3}>
            {/* ─── Brand block ─────────────────────────────────── */}
            <MotionBox variants={itemVariants}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: { xs: 13, sm: 13.5 },
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  color: '#3a2d20',
                  textTransform: 'uppercase',
                  display: 'inline-block',
                  position: 'relative',
                  pb: 0.75,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '2px',
                    background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
                  },
                }}
              >
                Hooten · Young
              </Typography>

              <Typography
                sx={{
                  mt: 1.5,
                  fontFamily: FONT_INTER,
                  fontSize: { xs: 10.5, sm: 11 },
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 10, 7, 0.55)',
                }}
              >
                Sales &amp; Marketing Intelligence Platform
              </Typography>
            </MotionBox>

            {/* ─── Title + subtitle ────────────────────────────── */}
            <MotionBox variants={itemVariants}>
              {/* Small Art Deco fleuron — short gold hairline + a
                  tiny diamond + matching hairline. Anchors the page
                  title without repeating the brand-block underline. */}
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 18px',
                    height: '1px',
                    background: `linear-gradient(to right, transparent, ${GOLD})`,
                  }}
                />
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    transform: 'rotate(45deg)',
                    bgcolor: GOLD,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    flex: '0 0 18px',
                    height: '1px',
                    background: `linear-gradient(to right, ${GOLD}, transparent)`,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: { xs: 28, sm: 32, md: 34, lg: 36 },
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.2,
                  color: INK,
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  sx={{
                    mt: 1.25,
                    fontFamily: FONT_INTER,
                    fontSize: { xs: 13.5, sm: 14 },
                    fontWeight: 400,
                    lineHeight: 1.55,
                    color: 'rgba(15, 10, 7, 0.62)',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </MotionBox>

            {/* ─── Form body ───────────────────────────────────── */}
            <MotionBox variants={itemVariants}>{children}</MotionBox>

            {/* ─── Page-level footer (Login → Signup link etc.) ─ */}
            {footer && (
              <MotionBox
                variants={itemVariants}
                sx={{
                  pt: 2,
                  borderTop: '1px solid rgba(15, 10, 7, 0.08)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: { xs: 13, sm: 13.5 },
                    color: 'rgba(15, 10, 7, 0.6)',
                    '& a': {
                      color: GOLD,
                      fontWeight: 600,
                      textDecoration: 'none',
                      borderBottom: '1px solid transparent',
                      transition: 'border-color 120ms ease',
                      '&:hover': { borderBottomColor: GOLD },
                    },
                  }}
                >
                  {footer}
                </Typography>
              </MotionBox>
            )}
          </Stack>
        </MotionBox>

        {/* ─── Editorial footer ───────────────────────────────
            Thin gold hairline above small-caps copyright. Anchors
            the bottom of the panel and fills the empty space that
            otherwise reads as "where's the page boundary?". */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 24, sm: 48, md: 64, lg: 80 },
            right: { xs: 24, sm: 48, md: 64, lg: 80 },
            bottom: { xs: 20, md: 28 },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              flex: '0 0 32px',
              height: '1px',
              background: `linear-gradient(to right, transparent, ${GOLD})`,
              opacity: 0.6,
            }}
          />
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 9.5,
              fontWeight: 500,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.4)',
              flexShrink: 0,
            }}
          >
            © Hooten · Young · Internal use only
          </Typography>
          <Box
            sx={{
              flex: 1,
              height: '1px',
              background: `linear-gradient(to right, ${GOLD}, transparent)`,
              opacity: 0.6,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
